// Generic cache for Intl.NumberFormat instances.
//
// Constructing an Intl.NumberFormat is relatively expensive, so callers that
// repeatedly format numbers with the same locale/options should reuse a
// single instance instead of constructing a new one on every call.
//
// The cache key is provided by the caller (typically derived from the
// locale and the format options that affect the formatter), and the
// formatter itself is only constructed on a cache miss. The number of
// distinct locale/option combinations used across the app is small and
// bounded, so entries are never evicted.
//
// This cache is shared by every caller in the codebase, so keys must be
// namespaced per call site (e.g. prefixed with the calling function's
// name) to avoid two unrelated call sites with differently-shaped options
// colliding on the same key and returning the wrong formatter.
const cache = new Map<string, Intl.NumberFormat>();

export function getNumberFormatter(
    key: string,
    create: () => Intl.NumberFormat
): Intl.NumberFormat {
    let formatter = cache.get(key);
    if (!formatter) {
        formatter = create();
        cache.set(key, formatter);
    }
    return formatter;
}

// Test support: the cache lives for the lifetime of the process, so a test
// that asserts on cache behaviour otherwise depends on whatever formatting
// happened to run before it. Clearing the cache lets such a test start from
// a known, empty state.
export function clearNumberFormatterCache(): void {
    cache.clear();
}
